import React, { useState, useEffect } from 'react'
import {
  MagnifyingGlassIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  UserIcon,
  BuildingOffice2Icon,
  GlobeAltIcon,
  CalendarDaysIcon,
  InformationCircleIcon,
  ArrowPathIcon,
  DocumentTextIcon,
  EyeIcon,
  ShieldExclamationIcon,
  ClockIcon,
  StarIcon
} from '@heroicons/react/24/outline'
import amlService from '../../services/amlService'
import AmlCategorizedSearchResults from '../../components/aml/AmlCategorizedSearchResults'
import AmlMatchDetailModal from '../../components/aml/AmlMatchDetailModal'
import { normalizeAmlSearchResults } from '../../utils/normalizeAmlSearchResults'
import {
  getAmlRiskBadgeColor as getRiskBadgeColor,
  getAmlConfidenceColor as getConfidenceColor,
  getAmlRiskLevelColor as getRiskLevelColor,
  getAmlThreatLevelColor as getThreatLevelColor
} from '../../utils/amlRiskColors'

const AMLScreening = () => {
  const [searchQuery, setSearchQuery] = useState('')
  const [searchType, setSearchType] = useState('all') // all, persons, entities
  const [isSearching, setIsSearching] = useState(false)
  const [searchResults, setSearchResults] = useState(null)
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(true)
  const [advancedFields, setAdvancedFields] = useState({
    country: 'AUST',
    dateOfBirth: '',
    idNumber: '',
    idType: ''
  })
  const [selectedMatch, setSelectedMatch] = useState(null)
  const [showDetailsModal, setShowDetailsModal] = useState(false)
  const [patronToScreen, setPatronToScreen] = useState('')
  const [screeningHistory, setScreeningHistory] = useState([])
  const [dashboardStats, setDashboardStats] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [dashboardLoadError, setDashboardLoadError] = useState(null)
  const [searchError, setSearchError] = useState(null)

  // Load dashboard stats on component mount
  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        setIsLoading(true)
        setDashboardLoadError(null)

        // Load dashboard stats and system information
        const [statsResp, dbStatus] = await Promise.all([
          amlService.getDashboardStats(),
          amlService.getDatabaseStatus()
        ])
        const statsData = statsResp?.data && typeof statsResp.data === 'object' ? statsResp.data : statsResp

        setDashboardStats({
          ...statsData,
          provider: statsData?.provider,
          lastUpdate:
            statsData?.lastUpdate ||
            statsData?.lastScreeningAt?.split?.('T')?.[0] ||
            null,
          databaseStatus: dbStatus
        })
      } catch (error) {
        console.error('Error loading dashboard data:', error)
        setDashboardStats(null)
        setDashboardLoadError(
          error?.message || 'Could not load AML screening statistics. Try again later.'
        )
      } finally {
        setIsLoading(false)
      }
    }

    loadDashboardData()
  }, [])

  const handleSearch = async () => {
    if (!searchQuery.trim()) return

    setIsSearching(true)
    setSearchResults(null)
    setSearchError(null)

    try {
      let results

      // Check if advanced search fields are provided
      const hasAdvancedFields = advancedFields.country || advancedFields.dateOfBirth || 
                               advancedFields.idNumber || advancedFields.idType

      let apiResponse
      if (hasAdvancedFields && searchType === 'persons') {
        apiResponse = await amlService.searchPersonsAdvanced(searchQuery, {
          limit: 50,
          includeInactive: true,
          country: advancedFields.country,
          dateOfBirth: advancedFields.dateOfBirth,
          idNumber: advancedFields.idNumber,
          idType: advancedFields.idType
        })
        results = normalizeAmlSearchResults(apiResponse, {
          query: searchQuery,
          searchType: 'advanced',
          advancedCriteria: advancedFields
        })
      } else {
        switch (searchType) {
        case 'persons':
          apiResponse = await amlService.searchPersons(searchQuery, {
            limit: 50,
            confidenceThreshold: 60,
            includeInactive: true
          })
          results = normalizeAmlSearchResults(apiResponse, { query: searchQuery, searchType: 'persons' })
          break

        case 'entities':
          apiResponse = await amlService.searchEntities(searchQuery, {
            limit: 50,
            confidenceThreshold: 60,
            includeInactive: true
          })
          results = normalizeAmlSearchResults(apiResponse, { query: searchQuery, searchType: 'entities' })
          break

        case 'all':
        default:
          apiResponse = await amlService.searchAll(searchQuery, {
            limit: 50,
            confidenceThreshold: 60,
            includeInactive: true,
            country: advancedFields.country,
            dateOfBirth: advancedFields.dateOfBirth,
            idNumber: advancedFields.idNumber,
            idType: advancedFields.idType
          })
          results = normalizeAmlSearchResults(apiResponse, { query: searchQuery, searchType: 'all' })
          break
        }
      }

      if (apiResponse?.success === false) {
        throw new Error(apiResponse?.error || 'Search failed')
      }

      setSearchResults(results)
    } catch (error) {
      console.error('Search error:', error)
      setSearchResults(null)
      setSearchError(error?.message || 'Search failed. Check AML service connectivity and try again.')
    } finally {
      setIsSearching(false)
    }
  }

  const handlePatronScreening = async () => {
    if (!patronToScreen.trim()) return

    try {
      const results = await amlService.screenPatron(patronToScreen, {
        // Additional patron data can be included here
        screeningType: 'comprehensive',
        source: 'manual_screening'
      })
      
      const newScreening = {
        id: results.screeningId || Date.now(),
        patronName: patronToScreen,
        timestamp: new Date().toISOString(),
        status: results.status || 'completed',
        matches: results.totalMatches || 0,
        highestRisk: results.highestRiskCategory || 'None',
        screeningResults: results
      }
      
      setScreeningHistory([newScreening, ...screeningHistory])
      setPatronToScreen('')
      
      if (results.totalMatches > 0) {
        alert(`Patron screening completed! Found ${results.totalMatches} matches. Highest risk: ${results.highestRiskCategory}`)
      } else {
        alert('Patron screening completed successfully! No matches found.')
      }
    } catch (error) {
      console.error('Patron screening error:', error)
      alert(error?.message || 'Patron screening failed. No screening was recorded.')
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center">
              <ShieldExclamationIcon className="h-8 w-8 text-red-600 mr-3" />
              AML/PEP Screening
            </h1>
            <p className="mt-2 text-gray-600">
              Search and screen against global sanctions, PEP, and watchlists
            </p>
          </div>
          <div className="text-right">
            <div className="text-sm text-gray-500">
              {isLoading
                ? 'Loading...'
                : dashboardStats?.provider === 'greenid'
                  ? 'GreenID watchlist · last screening'
                  : dashboardStats?.referenceDatabase === false
                    ? 'Last screening'
                    : 'Last reference DB update'}
            </div>
            {dashboardLoadError && (
              <div className="text-xs text-red-600 max-w-xs ml-auto text-right mt-1" title={dashboardLoadError}>
                Stats unavailable
              </div>
            )}
            <div className="text-lg font-semibold text-gray-900">
              {isLoading ? '...' : dashboardLoadError ? '—' : (dashboardStats?.lastUpdate || '—')}
            </div>
            {dashboardStats?.provider === 'greenid' && !dashboardLoadError && (
              <div className="text-xs text-gray-500 mt-1 max-w-xs ml-auto text-right">
                {typeof dashboardStats.totalScreenings === 'number'
                  ? `${dashboardStats.totalScreenings} screening run(s) on file`
                  : 'Live PEP/sanctions via GBG — no Dow Jones import'}
              </div>
            )}
            {dashboardStats?.databaseStatus && dashboardStats?.provider !== 'greenid' && (
              <div className="text-xs text-gray-500 mt-1">
                AML DB: {dashboardStats.databaseStatus.databases?.aml?.health?.status || 'Unknown'}
              </div>
            )}
          </div>
        </div>
      </div>

      {searchError && (
        <div
          className="mb-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
          role="alert"
        >
          <span className="font-medium">Search could not be completed. </span>
          {searchError}
        </div>
      )}

      {/* Search Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center">
            <MagnifyingGlassIcon className="h-6 w-6 text-gray-600 mr-2" />
            {showAdvancedSearch ? 'Advanced AML Search' : 'AML / PEP Search'}
          </h2>
          <button
            onClick={() => setShowAdvancedSearch(!showAdvancedSearch)}
            className="text-sm text-primary-600 hover:text-primary-700 flex items-center"
          >
            {showAdvancedSearch ? 'Simple Search' : 'Advanced Search'}
            <span className="ml-1">{showAdvancedSearch ? '↑' : '↓'}</span>
          </button>
        </div>
        
        <div className="space-y-4">
          {/* Search Type Selection */}
          <div className="flex space-x-4">
            <div className="flex items-center">
              <input
                type="radio"
                id="search-all"
                name="search-type"
                value="all"
                checked={searchType === 'all'}
                onChange={(e) => setSearchType(e.target.value)}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
              />
              <label htmlFor="search-all" className="ml-2 text-sm text-gray-700">
                All (Persons & Entities)
              </label>
            </div>
            <div className="flex items-center">
              <input
                type="radio"
                id="search-persons"
                name="search-type"
                value="persons"
                checked={searchType === 'persons'}
                onChange={(e) => setSearchType(e.target.value)}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
              />
              <label htmlFor="search-persons" className="ml-2 text-sm text-gray-700">
                Persons Only
              </label>
            </div>
            <div className="flex items-center">
              <input
                type="radio"
                id="search-entities"
                name="search-type"
                value="entities"
                checked={searchType === 'entities'}
                onChange={(e) => setSearchType(e.target.value)}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
              />
              <label htmlFor="search-entities" className="ml-2 text-sm text-gray-700">
                Entities Only
              </label>
            </div>
          </div>

          {/* Advanced Search Fields */}
          {showAdvancedSearch && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-4">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Additional Search Criteria</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Country Field */}
                <div>
                  <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-1">
                    Country
                  </label>
                  <input
                    type="text"
                    id="country"
                    value={advancedFields.country}
                    onChange={(e) => setAdvancedFields({...advancedFields, country: e.target.value})}
                    placeholder="e.g., AUST, PAKIS, IRAN, USA"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">Defaults to AUST. Use provider country codes (AUST, PAKIS, IRAN, etc.).</p>
                </div>

                {/* Date of Birth Field */}
                <div>
                  <label htmlFor="dateOfBirth" className="block text-sm font-medium text-gray-700 mb-1">
                    Date of Birth
                  </label>
                  <input
                    type="date"
                    id="dateOfBirth"
                    value={advancedFields.dateOfBirth}
                    onChange={(e) => setAdvancedFields({...advancedFields, dateOfBirth: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">Exact birth date match</p>
                </div>

                {/* ID Number Field */}
                <div>
                  <label htmlFor="idNumber" className="block text-sm font-medium text-gray-700 mb-1">
                    ID Number
                  </label>
                  <input
                    type="text"
                    id="idNumber"
                    value={advancedFields.idNumber}
                    onChange={(e) => setAdvancedFields({...advancedFields, idNumber: e.target.value})}
                    placeholder="Passport, SSN, etc."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">Any identification number</p>
                </div>

                {/* ID Type Field */}
                <div>
                  <label htmlFor="idType" className="block text-sm font-medium text-gray-700 mb-1">
                    ID Type
                  </label>
                  <select
                    id="idType"
                    value={advancedFields.idType}
                    onChange={(e) => setAdvancedFields({...advancedFields, idType: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="">Any Type</option>
                    <option value="National ID">National ID</option>
                    <option value="Passport No.">Passport</option>
                    <option value="Social Security No.">Social Security</option>
                    <option value="National Tax No.">Tax Number</option>
                    <option value="Email">Email</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">Specific ID document type</p>
                </div>
              </div>

              {/* Clear Advanced Fields */}
              <div className="flex justify-end">
                <button
                  onClick={() => setAdvancedFields({country: 'AUST', dateOfBirth: '', idNumber: '', idType: ''})}
                  className="text-sm text-gray-600 hover:text-gray-800"
                >
                  Reset Advanced Fields
                </button>
              </div>
            </div>
          )}

          {/* Search Input */}
          <div className="flex space-x-3">
            <div className="flex-1 relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="Enter name to search (e.g., Muhammad Hassan Ali)..."
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <button
              onClick={handleSearch}
              disabled={!searchQuery.trim() || isSearching}
              className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {isSearching ? (
                <>
                  <ArrowPathIcon className="h-5 w-5 mr-2 animate-spin" />
                  Searching...
                </>
              ) : (
                <>
                  <MagnifyingGlassIcon className="h-5 w-5 mr-2" />
                  Search
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Search Results */}
      {searchResults && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Search Results for "{searchResults.query}"
              </h2>
              {searchResults.searchType === 'advanced' && searchResults.advancedCriteria && (
                <div className="mt-1 text-sm text-gray-600">
                  <span className="font-medium">Advanced Search:</span>
                  {searchResults.advancedCriteria.country && (
                    <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                      Country: {searchResults.advancedCriteria.country}
                    </span>
                  )}
                  {searchResults.advancedCriteria.dateOfBirth && (
                    <span className="ml-2 px-2 py-1 bg-green-100 text-green-800 rounded text-xs">
                      DOB: {searchResults.advancedCriteria.dateOfBirth}
                    </span>
                  )}
                  {searchResults.advancedCriteria.idNumber && (
                    <span className="ml-2 px-2 py-1 bg-purple-100 text-purple-800 rounded text-xs">
                      ID: {searchResults.advancedCriteria.idNumber}
                    </span>
                  )}
                  {searchResults.advancedCriteria.idType && (
                    <span className="ml-2 px-2 py-1 bg-orange-100 text-orange-800 rounded text-xs">
                      Type: {searchResults.advancedCriteria.idType}
                    </span>
                  )}
                </div>
              )}
            </div>
            <div className="text-sm text-gray-500">
              {searchResults.totalFound} matches found in {searchResults.executionTime}
            </div>
          </div>

          {/* Results Summary */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center">
                <UserIcon className="h-8 w-8 text-blue-600" />
                <div className="ml-3">
                  <div className="text-lg font-semibold text-blue-900">
                    {searchResults.persons?.length || 0}
                  </div>
                  <div className="text-sm text-blue-600">Persons Found</div>
                </div>
              </div>
            </div>
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <div className="flex items-center">
                <BuildingOffice2Icon className="h-8 w-8 text-purple-600" />
                <div className="ml-3">
                  <div className="text-lg font-semibold text-purple-900">
                    {searchResults.entities?.length || 0}
                  </div>
                  <div className="text-sm text-purple-600">Entities Found</div>
                </div>
              </div>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center">
                <ExclamationTriangleIcon className="h-8 w-8 text-red-600" />
                <div className="ml-3">
                  <div className="text-lg font-semibold text-red-900">
                    {[...(searchResults.persons || []), ...(searchResults.entities || [])].filter(item => 
                      item.riskLevel === 'Critical' || item.threatLevel === 'High Threat'
                    ).length}
                  </div>
                  <div className="text-sm text-red-600">Critical Risk</div>
                </div>
              </div>
            </div>
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <div className="flex items-center">
                <ShieldExclamationIcon className="h-8 w-8 text-orange-600" />
                <div className="ml-3">
                  <div className="text-lg font-semibold text-orange-900">
                    {[...(searchResults.persons || []), ...(searchResults.entities || [])].filter(item => 
                      item.threatLevel === 'Political Risk' || item.threatLevel === 'Sanctioned Individual'
                    ).length}
                  </div>
                  <div className="text-sm text-orange-600">Special Interest</div>
                </div>
              </div>
            </div>
          </div>

          {/* Enhanced Threat Analysis */}
          {(searchResults.persons?.length > 0 || searchResults.entities?.length > 0) && (
            <div className="mb-6 p-4 bg-gradient-to-r from-gray-50 to-blue-50 border border-gray-200 rounded-lg">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                <span className="mr-2">📊</span>
                Threat Level Analysis
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {(() => {
                  const allMatches = [...(searchResults.persons || []), ...(searchResults.entities || [])];
                  const threatLevels = {
                    'Critical': allMatches.filter(item => item.riskLevel === 'Critical').length,
                    'High Threat': allMatches.filter(item => item.threatLevel === 'High Threat').length,
                    'Political Risk': allMatches.filter(item => item.threatLevel === 'Political Risk').length,
                    'Sanctioned': allMatches.filter(item => item.threatLevel === 'Sanctioned Individual').length
                  };
                  
                  return Object.entries(threatLevels).map(([level, count]) => (
                    count > 0 && (
                      <div key={level} className="text-center">
                        <div className={`text-lg font-bold ${
                          level === 'Critical' ? 'text-red-600' :
                          level === 'High Threat' ? 'text-red-500' :
                          level === 'Political Risk' ? 'text-orange-600' :
                          'text-purple-600'
                        }`}>
                          {count}
                        </div>
                        <div className="text-xs text-gray-600">{level}</div>
                      </div>
                    )
                  ));
                })()}
              </div>
            </div>
          )}

          {/* Persons Results — tabbed by PEP / Adverse Media / Associates when provider returns categories */}
          {searchResults.persons && searchResults.persons.length > 0 && (
            searchResults.resultsByCategory || searchResults.provider === 'greenid' ? (
              <AmlCategorizedSearchResults
                persons={searchResults.persons}
                resultsByCategory={searchResults.resultsByCategory}
                searchQuery={searchResults.query || searchQuery}
                provider={searchResults.provider}
                greenidVerificationId={searchResults.greenidVerificationId}
                greenidOverallStatus={searchResults.greenidOverallStatus}
                greenidProfilesMatchedCount={searchResults.greenidProfilesMatchedCount}
                screeningSummary={searchResults.screeningSummary}
                onViewDetails={(person) => {
                  setSelectedMatch(person)
                  setShowDetailsModal(true)
                }}
                getRiskBadgeColor={getRiskBadgeColor}
                getRiskLevelColor={getRiskLevelColor}
                getConfidenceColor={getConfidenceColor}
                getThreatLevelColor={getThreatLevelColor}
              />
            ) : (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                <UserIcon className="h-5 w-5 text-gray-600 mr-2" />
                Persons ({searchResults.persons.length})
              </h3>
              <div className="space-y-3">
                {searchResults.persons.map((person, idx) => (
                  <div
                    key={`person-${person.id ?? 'row'}-${idx}`}
                    className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <h4 className="text-lg font-semibold text-gray-900">{person.primaryName}</h4>
                          <span className={`px-2 py-1 text-xs font-medium rounded border ${getRiskBadgeColor(person.riskCategory)}`}>
                            {person.riskCategory}
                          </span>
                          {person.riskLevel && (
                            <span className={`px-2 py-1 text-xs font-bold rounded border ${getRiskLevelColor(person.riskLevel)}`}>
                              {person.riskLevel} Risk
                            </span>
                          )}
                          <div className={`flex items-center text-sm font-medium ${getConfidenceColor(person.confidence)}`}>
                            <StarIcon className="h-4 w-4 mr-1" />
                            {person.confidence}% match
                          </div>
                        </div>
                        
                        {/* Enhanced Risk Display */}
                        <div className="mb-2">
                          <p className="text-sm text-gray-600">{person.riskDescription}</p>
                          {person.threatLevel && person.threatLevel !== 'Standard' && (
                            <span className={`inline-block mt-1 px-2 py-1 text-xs font-medium rounded border ${getThreatLevelColor(person.threatLevel)}`}>
                              ⚠️ {person.threatLevel}
                            </span>
                          )}
                        </div>

                        {/* Risk Context */}
                        {person.riskContext && (
                          <div className="mb-2 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800">
                            <strong>Risk Context:</strong> {person.riskContext}
                          </div>
                        )}

                        {/* Risk Keywords */}
                        {person.riskKeywords && person.riskKeywords.length > 0 && (
                          <div className="mb-2">
                            <span className="text-xs font-medium text-gray-700">Risk Keywords: </span>
                            <div className="inline-flex flex-wrap gap-1 mt-1">
                              {person.riskKeywords.map((keyword, index) => (
                                <span key={index} className="px-2 py-1 text-xs bg-red-100 text-red-800 rounded border border-red-200">
                                  {keyword}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <span className="font-medium text-gray-700">Gender:</span>
                            <span className="ml-1 text-gray-600">{person.gender}</span>
                          </div>
                          <div>
                            <span className="font-medium text-gray-700">Countries:</span>
                            <span className="ml-1 text-gray-600">
                              {person.countries && person.countries.length > 0 ? person.countries.join(', ') : person.nationality}
                            </span>
                          </div>
                          <div>
                            <span className="font-medium text-gray-700">Status:</span>
                            <span className={`ml-1 ${person.active ? 'text-green-600' : 'text-red-600'}`}>
                              {person.active ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                          <div>
                            <span className="font-medium text-gray-700">Updated:</span>
                            <span className="ml-1 text-gray-600">{person.lastUpdated || 'N/A'}</span>
                          </div>
                        </div>

                        {/* Additional enriched data */}
                        {(person.sanctions && person.sanctions.length > 0) || (person.roles && person.roles.length > 0) && (
                          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                            {person.sanctions && person.sanctions.length > 0 && (
                              <div>
                                <span className="font-medium text-red-700">Sanctions:</span>
                                <div className="ml-1 text-red-600">
                                  {person.sanctions.slice(0, 2).map((sanction, index) => (
                                    <div key={index} className="text-xs">{sanction}</div>
                                  ))}
                                  {person.sanctions.length > 2 && (
                                    <div className="text-xs text-red-500">+{person.sanctions.length - 2} more</div>
                                  )}
                                </div>
                              </div>
                            )}
                            {person.roles && person.roles.length > 0 && (
                              <div>
                                <span className="font-medium text-blue-700">Roles:</span>
                                <div className="ml-1 text-blue-600">
                                  {person.roles.slice(0, 2).map((role, index) => (
                                    <div key={index} className="text-xs">{role}</div>
                                  ))}
                                  {person.roles.length > 2 && (
                                    <div className="text-xs text-blue-500">+{person.roles.length - 2} more</div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                        {person.aliases && person.aliases.length > 0 && (
                          <div className="mt-2">
                            <span className="text-sm font-medium text-gray-700">Aliases: </span>
                            <span className="text-sm text-gray-600">{person.aliases.join(', ')}</span>
                          </div>
                        )}
                      </div>
                      <div className="flex space-x-2 ml-4">
                        <button
                          onClick={() => {
                            setSelectedMatch(person)
                            setShowDetailsModal(true)
                          }}
                          className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center"
                        >
                          <EyeIcon className="h-4 w-4 mr-1" />
                          Details
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            )
          )}

          {/* Entities Results */}
          {searchResults.entities && searchResults.entities.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                <BuildingOffice2Icon className="h-5 w-5 text-gray-600 mr-2" />
                Entities ({searchResults.entities.length})
              </h3>
              <div className="space-y-3">
                {searchResults.entities.map((entity, idx) => (
                  <div
                    key={`entity-${entity.id ?? 'row'}-${idx}`}
                    className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <h4 className="text-lg font-semibold text-gray-900">{entity.primaryName}</h4>
                          <span className={`px-2 py-1 text-xs font-medium rounded border ${getRiskBadgeColor(entity.riskCategory)}`}>
                            {entity.riskCategory}
                          </span>
                          <div className={`flex items-center text-sm font-medium ${getConfidenceColor(entity.confidence)}`}>
                            <StarIcon className="h-4 w-4 mr-1" />
                            {entity.confidence}% match
                          </div>
                        </div>
                        <p className="text-sm text-gray-600 mb-2">{entity.riskDescription}</p>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                          <div>
                            <span className="font-medium text-gray-700">Status:</span>
                            <span className={`ml-1 ${entity.active ? 'text-green-600' : 'text-red-600'}`}>
                              {entity.active ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                          <div>
                            <span className="font-medium text-gray-700">Updated:</span>
                            <span className="ml-1 text-gray-600">{entity.lastUpdated}</span>
                          </div>
                          <div>
                            <span className="font-medium text-gray-700">Sanctions:</span>
                            <span className="ml-1 text-gray-600">
                              {entity.sanctions?.length > 0 ? entity.sanctions.length : 'None'}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex space-x-2 ml-4">
                        <button
                          onClick={() => {
                            setSelectedMatch(entity)
                            setShowDetailsModal(true)
                          }}
                          className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center"
                        >
                          <EyeIcon className="h-4 w-4 mr-1" />
                          Details
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Patron Screening Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
          <UserIcon className="h-6 w-6 text-gray-600 mr-2" />
          Patron Screening
        </h2>
        
        <div className="flex space-x-3 mb-4">
          <div className="flex-1">
            <input
              type="text"
              value={patronToScreen}
              onChange={(e) => setPatronToScreen(e.target.value)}
              placeholder="Enter patron name to screen..."
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <button
            onClick={handlePatronScreening}
            disabled={!patronToScreen.trim()}
            className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Screen Patron
          </button>
        </div>

        {/* Recent Screenings */}
        {screeningHistory.length > 0 && (
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Recent Screenings</h3>
            <div className="space-y-2">
              {screeningHistory.slice(0, 5).map((screening) => (
                <div key={screening.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <ClockIcon className="h-5 w-5 text-gray-600" />
                    <div>
                      <div className="font-medium text-gray-900">{screening.patronName}</div>
                      <div className="text-sm text-gray-600">
                        {new Date(screening.timestamp).toLocaleString()}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className="text-sm text-gray-600">{screening.matches} matches</span>
                    <span className={`px-2 py-1 text-xs font-medium rounded ${getRiskBadgeColor(screening.highestRisk)}`}>
                      {screening.highestRisk}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {showDetailsModal && selectedMatch && (
        <AmlMatchDetailModal
          match={selectedMatch}
          screeningSummary={searchResults?.screeningSummary}
          provider={searchResults?.provider}
          onClose={() => setShowDetailsModal(false)}
          getRiskBadgeColor={getRiskBadgeColor}
          getRiskLevelColor={getRiskLevelColor}
          getConfidenceColor={getConfidenceColor}
          getThreatLevelColor={getThreatLevelColor}
        />
      )}
    </div>
  )
}

export default AMLScreening